import * as z from 'zod';

import type {EmbroideryTextOptionForSelectorFragment} from '@/modules/commerce/embroidery-text/query';
import type {ItemWithVatTypeFragment} from '@/modules/commerce/item/query';

export const embroideryTextConfigurationSchema = z
	.object({
		colorId: z.string().nullable(),
		fontId: z.string(),
		text: z.string().nullable(),
	})
	.nullable();

export type EmbroideryTextConfiguration = z.infer<typeof embroideryTextConfigurationSchema>;

export function getDefaultEmbroideryTextConfiguration(
	_option: EmbroideryTextOptionForSelectorFragment,
): EmbroideryTextConfiguration {
	return null;
}

export function getInitialEmbroideryTextConfiguration(
	option: EmbroideryTextOptionForSelectorFragment,
): EmbroideryTextConfiguration {
	const fontId = option.defaultFont?._id ?? option.collection?.fonts?.at(0)?._id;
	if (!fontId) {
		return null;
	}

	return {
		colorId: option.colors?.at(0)?._id ?? null,
		fontId,
		text: option.defaultText ?? '',
	};
}

export function embroideryTextConfigurationFromFormData(
	option: EmbroideryTextOptionForSelectorFragment,
	prefix: string,
	formData: FormData,
): EmbroideryTextConfiguration {
	if (!option.available) {
		return null;
	}

	const enabled = formData.get(`${prefix}`) === 'enabled';

	if (!enabled) {
		return null;
	}

	let config: EmbroideryTextConfiguration = getInitialEmbroideryTextConfiguration(option);

	const colorId = formData.get(`${prefix}[colorId]`)?.toString() ?? null;
	const fontId = formData.get(`${prefix}[fontId]`)?.toString() ?? null;

	if (fontId) {
		config = {
			colorId,
			fontId,
			text: formData.get(`${prefix}[text]`)?.toString() ?? '',
		};
	}

	return config;
}

export function getItemsForEmbroideryTextConfiguration(
	option: EmbroideryTextOptionForSelectorFragment,
	configuration: EmbroideryTextConfiguration,
): ItemWithVatTypeFragment[] {
	const items: ItemWithVatTypeFragment[] = [];

	if (!configuration) {
		return items;
	}

	const {configuredFont} = getConfiguredEmbroideryText(option, configuration);

	if (configuredFont && option.items && option.items.length > 0) {
		items.push(...option.items);
	}

	return items;
}

export function getConfiguredEmbroideryText(
	option: EmbroideryTextOptionForSelectorFragment,
	configuration: EmbroideryTextConfiguration,
) {
	if (!option.available || !configuration) {
		return {
			configuredFont: null,
			configuredColor: null,
		};
	}

	const configuredFont = option.collection?.fonts?.find(
		(font) => font._id === configuration.fontId,
	);

	const configuredColor = option.colors?.find((color) => color._id === configuration.colorId);

	if (!configuredFont) {
		return {
			configuredFont: null,
			configuredColor,
		};
	}

	return {
		configuredFont,
		configuredColor,
	};
}
